"use client"

import {useCallback, useEffect, useMemo, useRef, useState} from "react"
import {useAppSelector} from "@/lib/hooks"
import {usePathname} from "@/i18n/navigation"
import {getOrchestrator} from "@/lib/dialog-orchestrator"
import {DialogsEnum} from "@/lib/enums/DialogsEnum"
import type {NativeInstallOutcome} from "@/app/components/notifications/PwaInstallPrompt"
import {DisplayMode, trackPwaInstalled, trackPwaOpened} from "@/lib/analytics/mixpanel-events"
import {safeLocalStorage} from "@/lib/utils/storage"

const DISMISSED_AT_PREFIX = "pwa-dismissed-at-"
const INSTALLED_KEY = "pwa-installed"
const TRIGGER_READY_PREFIX = "pwa-trigger-ready-"
const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000
const TRIGGER_DELAY_MS = 30_000

export const PWA_CASHIER_INTERACTION_EVENT = "pwa:cashier-open"

const BONUS_PATHS = ["/casino/bonuses", "/casino/vip", "/casino/cashback", "/casino/cryptobonus"]

interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>
    userChoice: Promise<{outcome: "accepted" | "dismissed"}>
}

function isDismissedWithin24h(userId: string): boolean {
    const ts = safeLocalStorage.getItem(DISMISSED_AT_PREFIX + userId)
    if (!ts) return false
    return Date.now() - Number(ts) < TWENTY_FOUR_HOURS_MS
}

function isAlreadyInstalled(): boolean {
    return !!safeLocalStorage.getItem(INSTALLED_KEY)
}

function getTriggerReady(userId: string): boolean {
    return !!safeLocalStorage.getItem(TRIGGER_READY_PREFIX + userId)
}

function persistTriggerReady(userId: string): void {
    safeLocalStorage.setItem(TRIGGER_READY_PREFIX + userId, "1")
}

function hasOpenDialog(): boolean {
    return !!document.querySelector("[data-radix-dialog-content]")
}

/**
 * How the app is being displayed. iOS Safari does not report `display-mode:
 * standalone` reliably and exposes `navigator.standalone` instead — hence the
 * spec's separate `standalone_ios` value.
 */
function resolveDisplayMode(): DisplayMode {
    if ((navigator as {standalone?: boolean}).standalone) return "standalone_ios"
    if (window.matchMedia("(display-mode: fullscreen)").matches) return "fullscreen"
    if (window.matchMedia("(display-mode: standalone)").matches) return "standalone"
    return "browser"
}

/**
 * Headless PWA-install manager.
 *
 * It owns *eligibility* (iOS detection, native `beforeinstallprompt` capture,
 * trigger readiness, 24h cooldown) and asks the orchestrator to show the
 * {@link PwaInstallPrompt} toast when appropriate. The orchestrator owns
 * ordering, mutual exclusion and rendering — replacing the old ModalQueue slot,
 * `activeId` gating and inline UI.
 */
export default function InstallPrompt() {
    const userId = useAppSelector(state => state.auth.userId)
    const pathname = usePathname()

    const [isIOS, setIsIOS] = useState(false)
    const [isAndroid, setIsAndroid] = useState(false)
    const [triggerReady, setTriggerReadyState] = useState(false)
    // Flips to true when beforeinstallprompt fires so the tryRequest effect re-runs.
    const [nativePromptReady, setNativePromptReady] = useState(false)

    const deferredPromptRef = useRef<BeforeInstallPromptEvent | null>(null)
    // True while a PWA request is live in the orchestrator (queued or visible).
    const requestedRef = useRef(false)

    /**
     * `pwa_installed` (spec §16) — reported from the browser's own `appinstalled`
     * event rather than from the toast's "accepted" outcome, so an install made
     * from the browser menu counts too. iOS fires nothing here: an iOS install is
     * only ever observable later, as a `standalone_ios` launch.
     */
    useEffect(() => {
        const onInstalled = () => trackPwaInstalled()
        window.addEventListener("appinstalled", onInstalled)
        return () => window.removeEventListener("appinstalled", onInstalled)
    }, [])

    /**
     * `pwa_opened` — only when the app really was launched from its icon. Firing
     * it for ordinary browser tabs would add an event per page load (Mixpanel
     * bills per event) and tell the funnel nothing: `platform` already says web.
     */
    useEffect(() => {
        const displayMode = resolveDisplayMode()
        if (displayMode !== "browser") trackPwaOpened(displayMode)
    }, [])

    // Detect platform and capture beforeinstallprompt event
    useEffect(() => {
        const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream
        const android = /Android/i.test(navigator.userAgent)
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setIsIOS(ios)
        setIsAndroid(android)
        if (ios) return

        const handler = (e: Event) => {
            e.preventDefault()
            deferredPromptRef.current = e as BeforeInstallPromptEvent
            setNativePromptReady(true)
        }
        window.addEventListener("beforeinstallprompt", handler)
        return () => window.removeEventListener("beforeinstallprompt", handler)
    }, [])

    // Restore triggerReady from localStorage on auth
    useEffect(() => {
        if (!userId) return
        if (getTriggerReady(userId)) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setTriggerReadyState(true)
        }
    }, [userId])

    // 30-second fallback trigger
    useEffect(() => {
        if (!userId) return
        if (getTriggerReady(userId)) return

        const id = window.setTimeout(() => {
            persistTriggerReady(userId)
            setTriggerReadyState(true)
        }, TRIGGER_DELAY_MS)

        return () => window.clearTimeout(id)
    }, [userId])

    // Pathname trigger: bonuses / vip / cashback sections
    useEffect(() => {
        if (!userId || triggerReady) return
        if (BONUS_PATHS.some(p => pathname.includes(p))) {
            persistTriggerReady(userId)
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setTriggerReadyState(true)
        }
    }, [pathname, userId, triggerReady])

    // Cashier interaction trigger (dispatched by WalletDialog)
    useEffect(() => {
        if (!userId || triggerReady) return

        const handler = () => {
            persistTriggerReady(userId)
            setTriggerReadyState(true)
        }

        window.addEventListener(PWA_CASHIER_INTERACTION_EVENT, handler)
        return () => window.removeEventListener(PWA_CASHIER_INTERACTION_EVENT, handler)
    }, [userId, triggerReady])

    // Reset the live flag whenever the toast closes, so it can re-trigger later.
    useEffect(() => {
        return getOrchestrator().on("closed", (event) => {
            if (event.id === DialogsEnum.PWA_INSTALL) requestedRef.current = false
        })
    }, [])

    // Cancel the toast when the user logs out
    useEffect(() => {
        if (!userId && requestedRef.current) {
            getOrchestrator().cancelById(DialogsEnum.PWA_INSTALL)
            requestedRef.current = false
        }
    }, [userId])

    // ── orchestrator-facing handlers passed as props on the request ──────────
    const requestNativeInstall = useCallback(async (): Promise<NativeInstallOutcome> => {
        const prompt = deferredPromptRef.current
        if (!prompt) return "unavailable"
        await prompt.prompt()
        const {outcome} = await prompt.userChoice
        deferredPromptRef.current = null
        if (outcome === "accepted") {
            safeLocalStorage.setItem(INSTALLED_KEY, "1")
            return "accepted"
        }
        return "dismissed"
    }, [])

    const markDismissed = useCallback(() => {
        if (userId) safeLocalStorage.setItem(DISMISSED_AT_PREFIX + userId, String(Date.now()))
    }, [userId])

    // Basic conditions that don't require DOM checks
    const canRequest = useMemo(() => {
        if (!userId) return false
        if (!triggerReady) return false
        return !pathname.includes("/games/");

    }, [userId, triggerReady, pathname])

    // Request the toast — retry via MutationObserver while a dialog is open so
    // the PWA prompt slots in only once the screen is otherwise clear.
    useEffect(() => {
        if (!canRequest) return

        const tryRequest = () => {
            if (requestedRef.current) return
            if (window.matchMedia("(display-mode: standalone)").matches) return
            if (isAlreadyInstalled()) return
            if (isDismissedWithin24h(userId!)) return
            if (hasOpenDialog()) return
            // Desktop requires the native prompt; iOS and Android always qualify.
            if (!deferredPromptRef.current && !isIOS && !isAndroid) return

            requestedRef.current = true
            getOrchestrator().open(DialogsEnum.PWA_INSTALL, {
                source: "system",
                // 3 s settling delay: lets page-level dialogs (e.g. WELCOME_BONUS_PROMO
                // queued after 1.5 s) enter the active set before this request is
                // eligible, preventing simultaneous display on returning sessions.
                delay: 3000,
                props: {
                    isIOS,
                    // Install button is shown only when the browser native prompt is
                    // available (Android/Chrome). Hidden on iOS and Android without prompt.
                    canNativeInstall: !!deferredPromptRef.current,
                    requestNativeInstall,
                    markDismissed,
                },
            })
        }

        tryRequest()
        if (requestedRef.current) return

        const observer = new MutationObserver(tryRequest)
        observer.observe(document.body, {childList: true})
        return () => observer.disconnect()
    }, [canRequest, userId, isIOS, isAndroid, nativePromptReady, requestNativeInstall, markDismissed])

    return null
}
